/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable indent */
/* eslint-disable operator-linebreak */
import dynamic from 'next/dynamic';
import { useCallback } from 'react';

import DynamicLoader from '@/atoms/dynamic-loader';
import ErrorBoundary from '@/atoms/error-boundary';
import MapMarker from '@/atoms/map-marker';
import { fromGeoJson } from '@/utils/coordinates';
import { GetOfficesListingQuery, OfficeDto } from '@graphql/hooks-and-types';

import MapMarkerContent from './map-marker-content';

const GoogleMap = dynamic(() => import('@/atoms/map'), {
  loading: () => (
    <div className="w-full h-full flex justify-center items-center">
      <DynamicLoader size="large" />
    </div>
  ),
});

const RentalMap: React.FC<{
  properties: GetOfficesListingQuery['getOfficesListing']['data'];
  isMapOpened: boolean;
  defaultCoordinates: { lat: number; lng: number };
  focusedPropertyId: string | null;
  setFocusedPropertyId: (id: string) => void;
}> = ({ properties, isMapOpened, defaultCoordinates, focusedPropertyId, setFocusedPropertyId }) => {
  const scrollToIndex = useCallback(
    (property: OfficeDto) => {
      setFocusedPropertyId(property.id);
      const index = properties.findIndex((item) => item.id === property.id);
      const propertiesList = document.getElementById('properties-list');
      if (propertiesList) {
        propertiesList.scrollTo({
          left: index * 284,
          top: Math.floor(index / (window.screen.width >= 1280 ? 2 : 1)) * 375,
          behavior: 'smooth',
        });
      }
    },
    [properties],
  );

  const isPropertyFocusedOnMap = useCallback(
    (propertyId: string) => {
      return propertyId === focusedPropertyId;
    },
    [focusedPropertyId],
  );

  return isMapOpened ? (
    <div
      className="mt-2 start-0 md:absolute md:top-[150px] md:start-1/2"
      style={{
        width: window.screen.width >= 768 ? window.screen.width / 2 - 60 : '100%',
        height: window.screen.width >= 768 ? window.screen.height * 0.7 : window.screen.height - 150,
      }}
    >
      <ErrorBoundary resetKeys={[defaultCoordinates, properties]}>
        <GoogleMap height="100%" center={defaultCoordinates} zoom={16}>
          {properties.map((property) => {
            // Skip listings with missing/dirty coordinates — feeding NaN to the
            // map projection throws InvalidValueError and crashes the page.
            const point = fromGeoJson(property.coordinates?.coordinates);
            if (!point) return null;
            return (
              <MapMarker
                onClick={() => scrollToIndex(property as OfficeDto)}
                isOpened
                isFocused={isPropertyFocusedOnMap(property.id)}
                key={property.id}
                lat={point.lat}
                lng={point.lng}
                content={<MapMarkerContent property={property} />}
              />
            );
          })}
        </GoogleMap>
      </ErrorBoundary>
    </div>
  ) : null;
};

export default RentalMap;
